// /Users/donovan68/dva-frontend-vite/src/pages/pro/ExecutionPage.tsx
// Step 3: Approval & Execution - Execution Enablement view
// UPDATED: New layout - Header above journey bar, breadcrumbs removed

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Target, TrendingUp, AlertCircle, BookOpen, GraduationCap, BarChart3, FileText } from "lucide-react";
import { ProProgressPathV3 } from "../../components/ProProgressPathV3";
import ProJourneyPageHeader from "../../components/journey/pro/ProJourneyPageHeader";
import ProspectPanel from "../../components/ProspectPanel";
import { Step3TabNav } from "../../components/Step3TabNav";
import { MessagesContent } from "../../components/MessagesContent";
import { FreemiumCard } from "../../components/FreemiumCard";
import { useFilter } from "../../contexts/FilterContext";
import { usePageContext } from "../../contexts/PageContext";
import { useClient } from "../../contexts/ClientContext";
import { useProspectList } from "../../hooks/useProspectList";
import { convertToProspectPanelFormat } from "../../utils/prospectUtils";

export default function ExecutionPage() {
  const navigate = useNavigate();
  const { activeFilter, setActiveFilter } = useFilter();
  const { setPageContext } = usePageContext();
  const { clientId } = useClient();
  const { prospects: rawProspects, updateStatus } = useProspectList(clientId || '');
  const prospects = convertToProspectPanelFormat(rawProspects);
  const messageCount = 4;

  // Handlers for ProspectPanel
  const handleProspectRemove = async (prospectId: string) => {
    if (!clientId) return;
    try {
      await updateStatus(Number(prospectId), 'qualified');
    } catch (err) {
      console.error('Failed to remove prospect:', err);
    }
  };

  const handleProfileClick = (prospectId: string) => {
    navigate(`/usecase/${prospectId}`);
  };

  const handleRoiClick = (prospectId: string) => {
    navigate(`/pro/analysis/${prospectId}`);
  };

  const handlePlanClick = (prospectId: string) => {
    navigate(`/pro/roadmap-planning`);
  };

  const handleReorder = (reordered: any[]) => {
    console.log('Prospects reordered:', reordered);
  };

  // Set page context - REMOVED breadcrumbs
  useEffect(() => {
    setPageContext({
      breadcrumbs: [],
      tabs: ["Tools", "Documents", "Messages"]
    });
  }, [setPageContext]);

  // TODO: Replace with real data from hooks/services
  const personalizationData = {
    strategy: 100,
    capabilities: 95,
    readiness: 90,
    docsCount: 8
  };

  // Handle phase navigation (updated for 3-step system)
  const handlePhaseClick = (phaseId: string) => {
    switch(phaseId) {
      case 'portfolio':
        navigate('/home');
        break;
      case 'analysis':
        navigate('/pro/analysis');
        break;
      case 'approval_execution':
        navigate('/pro/approval');
        break;
      default:
        navigate('/home');
    }
  };

  // DOCUMENTS CONTENT (3 cards for Execution)
  const documentsCards = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Dashboard Setup Guide",
      description: "Step-by-step instructions for deploying and configuring analytics dashboards.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Technical setup procedures",
        "Data connection guides",
        "Security configuration",
        "Performance optimization"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "5.2 MB"
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "User Training Materials",
      description: "Comprehensive training resources for end-users and administrators.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Quick start guides",
        "Video tutorial library",
        "Interactive workshops",
        "Certification programs"
      ],
      isPremium: false,
      fileType: "ZIP",
      fileSize: "12.4 MB"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Success Metrics Tracker",
      description: "Monitor adoption, usage, and impact of deployed initiatives.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "KPI tracking templates",
        "User adoption metrics",
        "ROI realization reports",
        "Continuous improvement tools"
      ],
      isPremium: false,
      fileType: "XLSX",
      fileSize: "3.8 MB"
    },
  ];

  const dashboards = [
    { id: 1, title: "Clinical Analytics Dashboard", status: "active", users: 45, updated: "2h ago" },
    { id: 2, title: "Population Health Metrics", status: "active", users: 32, updated: "5h ago" },
    { id: 3, title: "Quality Measures Tracker", status: "setup", users: 12, updated: "1d ago" },
  ];

  const alerts = [
    { id: 1, message: "Data refresh delayed for Clinical Analytics", severity: "warning", time: "1h ago" },
    { id: 2, message: "New users need training on Pop Health dashboard", severity: "info", time: "3h ago" },
  ];

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-4">
      {/* NEW LAYOUT ORDER: Tabs → Page Header → Journey Bar → Step3 Tabs → Content */}
      
      {/* 0. Content Tabs (Tools / Documents / Messages) - Same position as header tabs */}
      <div className="h-10 flex items-center justify-end gap-2 pt-3 mb-0.5">
        <nav className="flex items-center gap-2" aria-label="Section navigation">
          {['Tools', 'Documents', 'Messages'].map((tab) => {
            const active = tab === activeFilter;
            const isMessages = tab === 'Messages';
            const showBadge = isMessages && messageCount > 0;
            
            return (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18365E]
                  ${active ? "bg-[#18365E] text-white" : "text-[#18365E] hover:bg-black/5"}`}
                aria-current={active ? "page" : undefined}
              >
                {tab}
                {showBadge && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6E4C] text-[10px] font-bold text-white">
                    {messageCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
      
      {/* 1. Page Header */}
      <div className="mb-6 -mt-2">
        <ProJourneyPageHeader
          stepNumber={3}
          stepName="Approval & Execution"
          description="Deploy approved initiatives, train users, and track adoption metrics to ensure successful implementation."
        />
      </div>

      {/* 2. Journey Bar */}
      <div className="mb-6">
        <ProProgressPathV3 
          currentPhase="approval_execution"
          personalizationData={personalizationData}
          onPersonalizationClick={() => navigate("/personalize")}
          onPhaseClick={handlePhaseClick}
        />
      </div>

      {/* 3. Step 3 Tab Navigation (Roadmap / Approval / Execution) */}
      <div className="mb-6">
        <Step3TabNav />
      </div>

      {/* 4. Main content with fixed right panel */}
      <div className="flex gap-0">
        {/* LEFT: main content */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* Conditional content based on activeFilter */}
      {activeFilter === 'Messages' ? (
        <MessagesContent />
      ) : activeFilter === 'Documents' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documentsCards.map((card, idx) => (
            <FreemiumCard
              key={idx}
              icon={card.icon}
              title={card.title}
              description={card.description}
              contentType={card.contentType}
              listItems={card.listItems}
              isPremium={card.isPremium}
            />
          ))}
        </div>
      ) : (
        /* Tools view - Active dashboards and alerts (default) */
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl ring-1 ring-[#e5e7eb] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#18365E] mb-4">Active Dashboards</h2>
            <div className="space-y-4">
              {dashboards.map((dash) => (
                <div key={dash.id} className="p-4 rounded-xl bg-[#F5F7FA] hover:shadow-md transition-all">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-[#18365E]">{dash.title}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#717182]">
                        <span className="flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          {dash.users} users
                        </span>
                        <span>Updated {dash.updated}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      dash.status === 'active' 
                        ? 'bg-[#6FCF97]/10 text-[#6FCF97]' 
                        : 'bg-[#FF6E4C]/10 text-[#FF6E4C]'
                    }`}>
                      {dash.status}
                    </span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // For dashboards, navigate to resources without useCaseId (global view)
                        // In a real implementation, dashboards would have use_case_id
                        navigate('/pro/resources');
                      }}
                      className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors"
                    >
                      <BookOpen className="w-3 h-3" />
                      Resources
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl ring-1 ring-[#e5e7eb] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#18365E] mb-4">Alerts & Updates</h2>
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="p-3 rounded-xl bg-[#F5F7FA]">
                  <div className="flex items-start gap-2">
                    <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      alert.severity === 'warning' ? 'text-[#FF6E4C]' : 'text-[#18365E]'
                    }`} />
                    <div className="flex-1">
                      <p className="text-sm text-[#18365E] font-medium">{alert.message}</p>
                      <span className="text-xs text-[#717182]">{alert.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </>
          )}
        </div>

        {/* RIGHT: Prospect Panel - Fixed */}
        {clientId && (
          <div className="hidden lg:block">
            <ProspectPanel
              prospects={prospects}
              onRemove={handleProspectRemove}
              onProfileClick={handleProfileClick}
              onRoiClick={handleRoiClick}
              onPlanClick={handlePlanClick}
              onReorder={handleReorder}
            />
          </div>
        )}
      </div>
    </main>
  );
}